import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { LayoutService } from '../../../../core/services/layout.service';

// -- Types --------------------------------------------------------------------

/** mediaType enum from API: 0 = Video, 1 = Audio, 2 = Image+Audio */
export type ScheduledPromptMediaType = 'video' | 'audio' | 'image-audio';

export interface ValidateRecordingLinkData {
  isValid:          boolean;
  message:          string;
  schedulePromptId: string;
  questionId:       string;
  questionText:     string;
  promptTitle:      string;
  /** 0 = Video, 1 = Audio, 2 = Image+Audio */
  mediaType:        number;
  inviteeId:        string;
  /** 1 = Active, 2 = Already Used, 3 = Expired */
  linkStatus:       number;
}

// -- Component ----------------------------------------------------------------

@Component({
  selector: 'app-scheduled-prompt-record',
  templateUrl: './scheduled-prompt-record.html',
  styleUrl: './scheduled-prompt-record.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule],
})
export class ScheduledPromptRecord implements OnInit, OnDestroy {
  private readonly router        = inject(Router);
  private readonly route         = inject(ActivatedRoute);
  private readonly api           = inject(ApiService);
  private readonly layoutService = inject(LayoutService);
  private readonly cdr           = inject(ChangeDetectorRef);
  private readonly zone          = inject(NgZone);
  private readonly platformId    = inject(PLATFORM_ID);

  // -- Link validation -------------------------------------------------------

  readonly isValidating = signal(true);
  readonly linkInvalid  = signal(false);
  readonly linkError    = signal<string | null>(null);

  // -- Prompt data from API --------------------------------------------------

  readonly deliveryId       = signal('');
  readonly promptTitle      = signal('');
  readonly questionText     = signal('');
  readonly mediaTypeStr     = signal<ScheduledPromptMediaType>('video');
  readonly schedulePromptId = signal('');
  readonly questionId       = signal('');
  readonly inviteeId        = signal('');

  // -- Recording state -------------------------------------------------------

  readonly recordingState      = signal<'idle' | 'recording' | 'stopped'>('idle');
  readonly recordingTimer      = signal(0);
  readonly recordedUrl         = signal<string | null>(null);
  readonly uploadedUrl         = signal<string | null>(null);
  readonly uploadErrorMsg      = signal<string | null>(null);
  readonly audioUploadErrorMsg = signal<string | null>(null);
  readonly imageUploadErrorMsg = signal<string | null>(null);
  readonly permissionError     = signal<string | null>(null);
  readonly isPermissionDenied  = signal(false);
  readonly audioDragOver       = signal(false);
  readonly imageDragOver       = signal(false);
  readonly imagePreviewUrl     = signal<string | null>(null);
  readonly showReloadWarning   = signal(false);

  readonly canProceed = computed<boolean>(() =>
    this.recordingState() !== 'idle' || !!this.uploadedUrl() || !!this.recordedUrl(),
  );

  private imageFile: File | null = null;
  private liveStream:     MediaStream | null   = null;
  private mediaRecorder:  MediaRecorder | null = null;
  private recordedChunks: Blob[]               = [];
  private timerInterval:  ReturnType<typeof setInterval> | null = null;
  private keepMediaUrls   = false;
  private pendingNavAfterStop = false;

  readonly videoRef  = viewChild<ElementRef<HTMLVideoElement>>('videoPreview');
  readonly audioRef  = viewChild<ElementRef<HTMLAudioElement>>('audioPreview');
  readonly imageAudioInputRef = viewChild<ElementRef<HTMLInputElement>>('imageAudioInput');

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  // -- Lifecycle -------------------------------------------------------------

  async ngOnInit(): Promise<void> {
    this.layoutService.setHideHeader(true);
    const id = this.route.snapshot.paramMap.get('deliveryId') ?? '';
    this.deliveryId.set(id);
    await this.validateLink(id);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(false);
    this.stopLiveStream();
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (!this.keepMediaUrls) {
      const ru = this.recordedUrl();
      const uu = this.uploadedUrl();
      if (ru) URL.revokeObjectURL(ru);
      if (uu) URL.revokeObjectURL(uu);
    }
  }

  // -- Link validation -------------------------------------------------------

  private async validateLink(id: string): Promise<void> {
    if (!id) {
      this.linkError.set('Invalid link. Please check the link in your email and try again.');
      this.linkInvalid.set(true);
      this.isValidating.set(false);
      this.cdr.markForCheck();
      return;
    }

    try {
      const res = await this.api.request<ValidateRecordingLinkData>(
        'GET',
        `${API_ROUTES.VALIDATE_RECORDING_LINK}/${id}`,
        undefined,
        { showLoader: false, showToaster: false },
      );
      const data = res.data;
      if (!res.status || !data?.isValid) {
        this.setLinkStatusError(data?.linkStatus ?? 0, data?.message ?? res.message ?? '');
        return;
      }
      this.promptTitle.set(data.promptTitle);
      this.questionText.set(data.questionText);
      this.mediaTypeStr.set(this.mapMediaType(data.mediaType));
      this.schedulePromptId.set(data.schedulePromptId);
      this.questionId.set(data.questionId);
      this.inviteeId.set(data.inviteeId);

      window.addEventListener('beforeunload', this.onBeforeUnload);
      window.addEventListener('keydown', this.onKeyDown);
    } catch {
      this.linkError.set('Could not validate this recording link. Please try again later.');
      this.linkInvalid.set(true);
    } finally {
      this.isValidating.set(false);
      this.cdr.markForCheck();
    }
  }

  private setLinkStatusError(linkStatus: number, apiMessage: string): void {
    this.linkInvalid.set(true);
    switch (linkStatus) {
      case 2:
        this.linkError.set('This recording link has already been used. Your response was previously submitted.');
        break;
      case 3:
        this.linkError.set('This recording link has expired. Please contact the sender for a new link.');
        break;
      default:
        this.linkError.set(apiMessage || 'This recording link is no longer valid.');
    }
  }

  private mapMediaType(apiMediaType: number): ScheduledPromptMediaType {
    switch (apiMediaType) {
      case 0:  return 'video';
      case 1:  return 'audio';
      case 2:  return 'image-audio';
      default: return 'video';
    }
  }

  // -- Reload guard ----------------------------------------------------------

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  // -- Format helper ---------------------------------------------------------

  formatRecordingTime(seconds: number): string {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  // -- Recording: video ------------------------------------------------------

  async startVideoRecording(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    this.permissionError.set(null);
    this.isPermissionDenied.set(false);
    try {
      this.stopLiveStream();
      const prev = this.recordedUrl();
      if (prev) URL.revokeObjectURL(prev);
      this.recordedUrl.set(null);
      this.uploadedUrl.set(null);
      this.recordingTimer.set(0);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      this.liveStream = stream;

      const video = this.videoRef()?.nativeElement;
      if (video) {
        video.srcObject = stream;
        video.removeAttribute('src');
        video.controls = false;
        video.muted    = true;
        await video.play().catch(() => {});
      }

      this.recordedChunks = [];
      const mimeType = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) this.recordedChunks.push(e.data); };
      mr.onstop = () => this.onRecordingStop();
      mr.start(100);
      this.mediaRecorder = mr;

      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => this.recordingTimer.update(t => t + 1), 1000);
      this.zone.run(() => { this.recordingState.set('recording'); this.cdr.markForCheck(); });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        this.isPermissionDenied.set(true);
        this.permissionError.set(
          'Camera or microphone access was blocked. Please allow access in your browser settings and reload the page.',
        );
      } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        this.permissionError.set('No camera or microphone found. Please connect a device and try again.');
      } else {
        this.permissionError.set('Could not access camera or microphone. Please check your device.');
      }
      this.zone.run(() => this.cdr.markForCheck());
    }
  }

  retakeRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.stopLiveStream();
    const prev = this.recordedUrl();
    if (prev) URL.revokeObjectURL(prev);
    this.recordedUrl.set(null);
    this.recordedChunks = [];
    this.recordingTimer.set(0);
    this.recordingState.set('idle');
    setTimeout(() => this.startVideoRecording(), 100);
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
  }

  // -- Recording: audio ------------------------------------------------------

  async startAudioRecording(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    this.permissionError.set(null);
    this.isPermissionDenied.set(false);
    try {
      this.stopLiveStream();
      const prev = this.recordedUrl();
      if (prev) URL.revokeObjectURL(prev);
      this.recordedUrl.set(null);
      this.uploadedUrl.set(null);
      this.recordingTimer.set(0);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.liveStream = stream;

      this.recordedChunks = [];
      const mimeType =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) this.recordedChunks.push(e.data); };
      mr.onstop = () => this.onRecordingStop();
      mr.start(100);
      this.mediaRecorder = mr;

      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => this.recordingTimer.update(t => t + 1), 1000);
      this.zone.run(() => { this.recordingState.set('recording'); this.cdr.markForCheck(); });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        this.isPermissionDenied.set(true);
        this.permissionError.set(
          'Microphone access was blocked. Please allow access in your browser settings and reload the page.',
        );
      } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        this.permissionError.set('No microphone found. Please connect a device and try again.');
      } else {
        this.permissionError.set('Could not access microphone. Please check your device.');
      }
      this.zone.run(() => this.cdr.markForCheck());
    }
  }

  retakeAudioRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.stopLiveStream();
    const prev = this.recordedUrl();
    if (prev) URL.revokeObjectURL(prev);
    this.recordedUrl.set(null);
    this.recordedChunks = [];
    this.recordingTimer.set(0);
    this.recordingState.set('idle');
    setTimeout(() => this.startAudioRecording(), 100);
  }

  // -- Upload handlers -------------------------------------------------------

  onVideoUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      this.uploadErrorMsg.set('Only video files are allowed (MP4, MOV, WebM, etc.).');
      input.value = '';
      return;
    }
    const MAX_SIZE = 1 * 1024 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(0);
      this.uploadErrorMsg.set(`File is too large (${sizeMb} MB). Maximum allowed size is 1 GB.`);
      input.value = '';
      return;
    }
    this.uploadErrorMsg.set(null);
    const prev = this.uploadedUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url);
    this.recordedUrl.set(null);
    this.recordingState.set('stopped');
    const video = this.videoRef()?.nativeElement;
    if (video) { video.srcObject = null; video.src = url; video.controls = true; video.muted = false; video.load(); }
  }

  onAudioUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      this.audioUploadErrorMsg.set('Only audio files are allowed (MP3, WAV, AAC, M4A, etc.).');
      input.value = '';
      return;
    }
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      this.audioUploadErrorMsg.set(`File too large. Maximum allowed size is 500 MB.`);
      input.value = '';
      return;
    }
    this.audioUploadErrorMsg.set(null);
    const prev = this.uploadedUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url);
    this.recordedUrl.set(null);
    this.recordingState.set('stopped');
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.src = url; audio.load(); }
  }

  onImageForAudioUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.imageUploadErrorMsg.set('Only image files are allowed (JPEG, PNG, WebP, etc.).');
      input.value = '';
      this.cdr.markForCheck();
      return;
    }
    this.imageUploadErrorMsg.set(null);
    this.imageFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreviewUrl.set(e.target?.result as string);
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  removeImageForAudio(): void {
    this.imagePreviewUrl.set(null);
    this.imageFile = null;
  }

  // -- Drag and drop ---------------------------------------------------------

  onAudioDragOver(event: DragEvent): void  { event.preventDefault(); event.stopPropagation(); this.audioDragOver.set(true); }
  onAudioDragLeave(event: DragEvent): void { event.preventDefault(); this.audioDragOver.set(false); }

  onAudioDrop(event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.audioDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      this.audioUploadErrorMsg.set('Only audio files are allowed (MP3, WAV, AAC, M4A, etc.).');
      this.cdr.markForCheck(); return;
    }
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) { this.audioUploadErrorMsg.set('File too large. Maximum allowed size is 500 MB.'); this.cdr.markForCheck(); return; }
    this.audioUploadErrorMsg.set(null);
    const prev = this.uploadedUrl(); if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url); this.recordedUrl.set(null); this.recordingState.set('stopped');
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.src = url; audio.load(); }
    this.cdr.markForCheck();
  }

  onImageAreaDragOver(event: DragEvent): void  { event.preventDefault(); event.stopPropagation(); this.imageDragOver.set(true); }
  onImageAreaDragLeave(event: DragEvent): void { event.preventDefault(); this.imageDragOver.set(false); }

  onImageAreaDrop(event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.imageDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      this.imageFile = file;
      const reader = new FileReader();
      reader.onload = (e) => { this.imagePreviewUrl.set(e.target?.result as string); this.cdr.markForCheck(); };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('audio/')) {
      const MAX_SIZE = 500 * 1024 * 1024;
      if (file.size > MAX_SIZE) { this.audioUploadErrorMsg.set('File too large. Maximum allowed size is 500 MB.'); this.cdr.markForCheck(); return; }
      this.audioUploadErrorMsg.set(null);
      const prev = this.uploadedUrl(); if (prev) URL.revokeObjectURL(prev);
      const url = URL.createObjectURL(file);
      this.uploadedUrl.set(url); this.recordedUrl.set(null); this.recordingState.set('stopped');
      const audio = this.audioRef()?.nativeElement;
      if (audio) { audio.src = url; audio.load(); }
      this.cdr.markForCheck();
    }
  }

  // -- Navigation to review --------------------------------------------------

  goToReview(): void {
    if (this.recordingState() === 'recording') {
      this.pendingNavAfterStop = true;
      this.stopRecording();
      return;
    }
    this.navigateToReview();
  }

  private navigateToReview(): void {
    this.keepMediaUrls = true;
    const mediaUrl   = this.uploadedUrl() || this.recordedUrl();
    const imageUrl   = this.imagePreviewUrl();
    const deliveryId = this.deliveryId();

    if (isPlatformBrowser(this.platformId)) {
      if (mediaUrl) sessionStorage.setItem('fm_sp_review_video_url', mediaUrl);
      else          sessionStorage.removeItem('fm_sp_review_video_url');
      if (imageUrl) sessionStorage.setItem('fm_sp_review_image_url', imageUrl);
      else          sessionStorage.removeItem('fm_sp_review_image_url');
      sessionStorage.setItem('fm_sp_media_type',         this.mediaTypeStr());
      sessionStorage.setItem('fm_sp_schedule_prompt_id', this.schedulePromptId());
      sessionStorage.setItem('fm_sp_question_id',        this.questionId());
      sessionStorage.setItem('fm_sp_invitee_id',         this.inviteeId());
      sessionStorage.setItem('fm_sp_question_text',      this.questionText());
      sessionStorage.setItem('fm_sp_prompt_title',       this.promptTitle());
      sessionStorage.setItem('fm_sp_delivery_id',        deliveryId);
      sessionStorage.setItem('fm_sp_navigated',          '1');
    }

    this.router.navigate(['/app/scheduleprompt/record', deliveryId, 'review'], {
      state: {
        videoUrl:         mediaUrl,
        imageUrl,
        mediaType:        this.mediaTypeStr(),
        schedulePromptId: this.schedulePromptId(),
        questionId:       this.questionId(),
        inviteeId:        this.inviteeId(),
        questionText:     this.questionText(),
        promptTitle:      this.promptTitle(),
        deliveryId,
        videoDuration:    this.recordingTimer(),
      },
    });
  }

  // -- Private helpers -------------------------------------------------------

  private onRecordingStop(): void {
    const isAudio = this.mediaTypeStr() === 'audio' || this.mediaTypeStr() === 'image-audio';
    const blobType = this.mediaRecorder?.mimeType || (isAudio ? 'audio/webm' : 'video/webm');
    const blob = new Blob(this.recordedChunks, { type: blobType });
    const url  = URL.createObjectURL(blob);
    this.recordedUrl.set(url);
    if (!isAudio) {
      const video = this.videoRef()?.nativeElement;
      if (video) { video.srcObject = null; video.src = url; video.controls = true; video.muted = false; video.load(); }
    } else {
      const audio = this.audioRef()?.nativeElement;
      if (audio) { audio.src = url; audio.load(); }
    }
    this.stopLiveStream();
    this.zone.run(() => {
      this.recordingState.set('stopped');
      this.cdr.markForCheck();
    });
    if (this.pendingNavAfterStop) {
      this.pendingNavAfterStop = false;
      setTimeout(() => this.navigateToReview(), 0);
    }
  }

  private stopLiveStream(): void {
    this.liveStream?.getTracks().forEach(t => t.stop());
    this.liveStream   = null;
    this.mediaRecorder = null;
  }
}
